import { useMutation, useQuery } from "@tanstack/react-query";
import { Invoice, IPlan, IProration } from "~/types/billing";
import { INVOICES, PLANS, PRORATION } from "./types";
import {
  shopifySubscription,
  getPlans,
  stripeSubscription,
  updateCard,
  cancelSubscription,
  getInvoices,
  getProration,
  stripeIntent,
  cancellationInfo,
  paypalSubscription,
  sendFailureReason,
} from "./requests";

export const usePlans = () =>
  useQuery<Array<IPlan>>(PLANS, getPlans, {
    // Plans rarely change mid-session; cache for 5 minutes to avoid
    // redundant fetches when multiple components mount (e.g. PageWrapper + PlansModal).
    // Mutations that affect plans already call invalidateQueries(PLANS).
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

export const useInvoices = () =>
  useQuery<Array<Invoice>>(INVOICES, () => getInvoices(), {
    staleTime: 0, // billing data must always be fresh
  });

export const useShopifySubscription = () => useMutation(shopifySubscription);

export const usePaypalSubscription = () => useMutation(paypalSubscription);

export const useStripeSubscription = () => useMutation(stripeSubscription);

export const useStripeIntent = () => useMutation(stripeIntent);

export const useUpdateCard = () => useMutation(updateCard);

export const useCancelSubscription = () => useMutation(cancelSubscription);

export const useProrationValue = (planId?: string, promoCodeId?: string) =>
  useQuery<IProration>(
    [PRORATION, planId],
    () => getProration(planId, promoCodeId),
    {
      staleTime: 0, // proration values change with plan/promo — always fresh
      enabled: !!planId, // don't fire when planId is undefined
      retry: false, // don't hammer the endpoint if a lookup fails; page falls back to plan price
    }
  );

export const useCancellationInfo = () => useMutation(cancellationInfo);

export const useSendFailureReason = () => useMutation(sendFailureReason);
